import React, { useState, useContext, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Search,
  Heart,
  User,
  ShoppingBag,
  LayoutGrid,
  HeartPulse,
  Package,
  Users,
  Info,
  ChevronRight,
  ChevronDown,
  Cross,
  X,
} from "lucide-react";

import { useSiteConfig } from "../context/SiteConfigContext";
import { useCart } from "../context/CartContext";
import { CategoryContext } from "../context/CategoryContext";
import Auth from "../utils/AuthUtils";

import CartCanvas from "../pages/canvas/CartCanvas";
import LoginCanvas from "../pages/canvas/LoginCanvas";
import RegisterCanvas from "../pages/canvas/RegisterCanvas";
import SearchCanvas from "../pages/canvas/SearchCanvas";

const MEGA_WIDTH = 680;
const HIDE_AFTER = 80; // itna scroll hone ke baad hi header hide hoga
const SCROLL_DELTA = 8; // chhote jitter ko ignore karne ke liye

export default function Header() {
  const { config } = useSiteConfig();
  const { cartCount } = useCart();
  const { categories = [] } = useContext(CategoryContext);
  const { pathname } = useLocation();
  const isLoggedIn = Auth.getToken();

  const [alertVisible, setAlert] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [openId, setOpenId] = useState(null); // mobile accordion (main categories)
  const [mobileCatOpen, setMobileCatOpen] = useState(false); // mobile "Categories"
  const [catOpen, setCatOpen] = useState(false); // desktop dropdown
  const [activeCatId, setActiveCatId] = useState(null); // desktop: hovered main category
  const [megaLeft, setMegaLeft] = useState(0);
  const [hidden, setHidden] = useState(false); // header scroll pe hide

  const subnavRef = useRef(null);
  const catBtnRef = useRef(null);
  const lastY = useRef(0);
  const ticking = useRef(false);

  const handleToggle = (id) => setOpenId(openId === id ? null : id);

  // Ctrl/Cmd + K -> search offcanvas open
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const el = document.getElementById("searchOffcanvas");
        if (el && window.bootstrap?.Offcanvas) {
          window.bootstrap.Offcanvas.getOrCreateInstance(el).show();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Scroll down -> header hide, scroll up -> header show
  useEffect(() => {
    lastY.current = window.scrollY;

    const update = () => {
      const y = window.scrollY;
      const diff = y - lastY.current;

      if (y < HIDE_AFTER) {
        setHidden(false); // top pe hamesha visible
        lastY.current = y;
      } else if (Math.abs(diff) >= SCROLL_DELTA) {
        setHidden(diff > 0); // neeche = hide, upar = show
        lastY.current = y;
      }
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(update);
        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Header hide ho to dropdown bhi band
  useEffect(() => {
    if (hidden) setCatOpen(false);
  }, [hidden]);

  // Route change par dropdown band + header wapas dikhao
  useEffect(() => {
    setCatOpen(false);
    setHidden(false);
  }, [pathname]);

  // Bahar click / Esc par dropdown band
  useEffect(() => {
    const onDown = (e) => {
      if (subnavRef.current && !subnavRef.current.contains(e.target)) {
        setCatOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setCatOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  if (!config) return null;

  // Nav items: sab static — "Categories" ek dropdown item hai
  const navItems = [
    { id: "home", label: "Home", path: "/", icon: <LayoutGrid size={17} /> },
    { id: "shop", label: "Shop", path: "/shop", icon: <Package size={17} /> },
    {
      id: "categories",
      label: "Categories",
      path: "/categories",
      icon: <HeartPulse size={17} />,
      dropdown: true,
    },
    { id: "about", label: "About", path: "/about-us", icon: <Info size={17} /> },
    { id: "contact", label: "Contact", path: "/contact-us", icon: <Users size={17} /> },
  ];

  const isActive = (item) =>
    item.path === "/" ? pathname === "/" : pathname.startsWith(item.path);

  const activeItem = navItems.find(isActive);

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);

  const closeMega = () => setCatOpen(false);

  // Dropdown ko Categories button ke neeche align karo
  const openMega = () => {
    if (catBtnRef.current && subnavRef.current) {
      const btn = catBtnRef.current.getBoundingClientRect();
      const wrap = subnavRef.current.getBoundingClientRect();
      const left = btn.left - wrap.left;
      const maxLeft = Math.max(0, wrap.width - Math.min(MEGA_WIDTH, wrap.width - 16));
      setMegaLeft(Math.max(0, Math.min(left, maxLeft)));
    }
    setCatOpen(true);
  };

  const toggleMega = () => (catOpen ? closeMega() : openMega());

  const currentCat =
    categories.find((c) => c._id === activeCatId) || categories[0] || null;

  // Dropdown khula ho to header hide nahi hoga
  const headerHidden = hidden && !catOpen;

  return (
    <>
      <header className={`med-header-root ${headerHidden ? "is-hidden" : ""}`}>
        {/* ── Alert Bar ── */}
        {alertVisible && config?.alertMessage && (
          <div className="med-alert-bar">
            <div className="container-fluid px-4">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <span className="alert-icon">!</span>
                  <span>{config.alertMessage}</span>
                  <span className="alert-badge">Notice</span>
                </div>
                <button
                  className="alert-dismiss"
                  onClick={() => setAlert(false)}
                  aria-label="Dismiss alert"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Main Topbar ── */}
        <nav className="med-topbar">
          <div className="container-fluid px-4 h-100">
            <div className="d-flex align-items-center justify-content-between h-100 gap-3">
              {/* Left — Logo + Brand */}
              <div className="d-flex align-items-center gap-3 flex-shrink-0">
                <button
                  className="med-hamburger d-md-none"
                  aria-label="Toggle menu"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#mobileMenu"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </button>

                <Link to="/" className="d-flex align-items-center gap-2 text-decoration-none">
                  <div className="med-logo-mark">
                    {config?.logo ? (
                      <img
                        src={config.logo}
                        alt="logo"
                        style={{ width: 82, height: 82, objectFit: "contain" }}
                      />
                    ) : (
                      <Cross size={18} />
                    )}
                  </div>
                  <div>
                    <div className="med-brand-name">{config?.siteName || "Disha"}</div>
                    <div className="med-brand-name">{config?.siteName || "Inc."}</div>
                    
                  </div>
                </Link>

                <div className="med-vdivider d-none d-md-block"></div>

                <div className="med-status-pill online d-none d-md-flex">
                  <span className="dot"></span>
                  Systems Online
                </div>
              </div>

              {/* Center — Search */}
              <div className="d-none d-md-flex flex-grow-1 justify-content-center">
                <button
                  type="button"
                  className="med-search-wrap"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#searchOffcanvas"
                  aria-label="Open search"
                >
                  <Search size={16} className="med-search-icon" />
                  <span className="med-search-placeholder">
                    Search products, categories…
                  </span>
                  <span className="med-search-shortcut">
                    <kbd>{isMac ? "⌘" : "Ctrl"}</kbd>
                    <kbd>K</kbd>
                  </span>
                </button>
              </div>

              {/* Right — Actions + User */}
              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {/* Mobile search */}
                <button
                  className="med-icon-btn d-md-none"
                  aria-label="Search"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#searchOffcanvas"
                >
                  <Search size={17} />
                </button>

                {/* Cart */}
                <button
                  className="med-icon-btn"
                  aria-label="Cart"
                  title="Cart"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#shoppingCartCanvas"
                >
                  <ShoppingBag size={17} />
                  {cartCount > 0 && <span className="med-notif-count">{cartCount}</span>}
                </button>

                {/* Wishlist */}
                <Link
                  to="/wishlist"
                  className="med-icon-btn d-none d-sm-flex"
                  aria-label="Wishlist"
                  title="Wishlist"
                >
                  <Heart size={17} />
                </Link>

                {/* Account */}
                {/* <Link
                  to={isLoggedIn ? "/users/dashboard" : "/users/login"}
                  className="med-icon-btn d-none d-sm-flex"
                  aria-label="Account"
                  title="Account"
                >
                  <User size={17} />
                </Link> */}

                <div className="med-vdivider d-none d-sm-block"></div>

                <Link
                  to={isLoggedIn ? "/users/dashboard" : "/users/login"}
                  className="d-flex align-items-center gap-2 text-decoration-none"
                >
                  <div className="med-user-info d-none d-lg-block text-end">
                    <div className="name">{isLoggedIn ? "My Account" : "Guest"}</div>
                    <div className="role">{isLoggedIn ? "Dashboard" : "Login / Register"}</div>
                  </div>
                  <div className="med-avatar" title={isLoggedIn ? "Dashboard" : "Login"}>
                    <User size={16} />
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* ── Sub Navigation (hamburger aane par hide, hamburger na ho tab show) ── */}
        <div
          className="med-subnav d-none d-md-block"
          ref={subnavRef}
          onMouseLeave={closeMega}
        >
          <div className="container-fluid px-4">
            <div className="med-subnav-scroll">
              <nav
                className="d-flex align-items-center"
                role="navigation"
                aria-label="Main navigation"
              >
                {navItems.map((item) =>
                  item.dropdown ? (
                    <button
                      key={item.id}
                      ref={catBtnRef}
                      type="button"
                      className={`med-nav-item med-nav-dropdown-btn ${
                        isActive(item) || catOpen ? "active" : ""
                      }`}
                      aria-haspopup="true"
                      aria-expanded={catOpen}
                      onMouseEnter={openMega}
                      onClick={toggleMega}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                      <ChevronDown
                        size={14}
                        className={`med-nav-caret ${catOpen ? "open" : ""}`}
                      />
                    </button>
                  ) : (
                    <Link
                      key={item.id}
                      to={item.path}
                      className={`med-nav-item ${isActive(item) ? "active" : ""}`}
                      onMouseEnter={closeMega}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  )
                )}
              </nav>
            </div>
          </div>

          {/* ── Categories Dropdown (2-pane) ── */}
          {catOpen && (
            <div
              className="med-mega"
              role="menu"
              style={{ left: megaLeft, width: MEGA_WIDTH }}
            >
              {!currentCat ? (
                <div className="med-mega-empty">No categories available</div>
              ) : (
                <>
                  {/* Left: main categories */}
                  <div className="med-mega-left">
                    <div className="med-mega-label">Browse categories</div>
                    {categories.map((cat) => (
                      <Link
                        key={cat._id}
                        to={`/categories/${cat.slugPath}`}
                        className={`med-mega-cat ${
                          currentCat._id === cat._id ? "active" : ""
                        }`}
                        onMouseEnter={() => setActiveCatId(cat._id)}
                        onFocus={() => setActiveCatId(cat._id)}
                        onClick={closeMega}
                      >
                        <span>{cat.name}</span>
                        {cat.children?.length > 0 && <ChevronRight size={14} />}
                      </Link>
                    ))}
                  </div>

                  {/* Right: sub categories of hovered category */}
                  <div className="med-mega-right">
                    <div className="med-mega-head">
                      <h6>{currentCat.name}</h6>
                      <Link
                        to={`/categories`}
                        className="med-mega-viewall"
                        onClick={closeMega}
                      >
                        View all <ChevronRight size={13} />
                      </Link>
                    </div>

                    {currentCat.children?.length > 0 ? (
                      <div className="med-mega-subgrid">
                        {currentCat.children.map((sub) => (
                          <Link
                            key={sub._id}
                            to={`/categories/${sub.slugPath}`}
                            className="med-mega-sub"
                            onClick={closeMega}
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <div className="med-mega-empty">No sub categories yet</div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Breadcrumb Bar ── */}
        {/* <div className="med-breadcrumb-bar">
          <div className="container-fluid px-4">
            <div className="d-flex align-items-center justify-content-between">
              <nav aria-label="Breadcrumb" className="d-flex align-items-center">
                <Link to="/" className="med-breadcrumb-item">
                  Home
                </Link>
                <span className="med-breadcrumb-sep">
                  <ChevronRight size={12} />
                </span>
                <span className="med-breadcrumb-item">{activeItem?.label || "Page"}</span>
                <span className="med-breadcrumb-sep">
                  <ChevronRight size={12} />
                </span>
                <span className="med-breadcrumb-item current">Overview</span>
              </nav>
              {config?.envLabel && <span className="med-env-tag">{config.envLabel}</span>}
            </div>
          </div>
        </div> */}
      </header>

      {/* SEARCH OFFCANVAS */}
      <div
        className="offcanvas offcanvas-top custom-search-offcanvas"
        id="searchOffcanvas"
        tabIndex="-1"
      >
        <div className="search-offcanvas-inner">
          <button
            className="search-close-btn"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={() => {
              setSearchText("");
              setShowSearchModal(false);
            }}
          >
            <X size={22} />
          </button>

          <SearchCanvas
            isOpen={showSearchModal}
            searchText={searchText}
            setSearchText={(val) => {
              setSearchText(val);
              setShowSearchModal(true);
            }}
          />
        </div>
      </div>

      {/* CART OFFCANVAS */}
      <div className="offcanvas offcanvas-end custom-cart-offcanvas" id="shoppingCartCanvas">
        <CartCanvas />
      </div>

      {/* LOGIN OFFCANVAS */}
      <div
        className="offcanvas offcanvas-end popup-style-1 popup-shopping-cart"
        id="loginOffCanvas"
      >
        <LoginCanvas />
      </div>

      {/* REGISTER OFFCANVAS */}
      <div
        className="offcanvas offcanvas-end popup-style-1 popup-shopping-cart"
        id="registerOffCanvas"
      >
        <RegisterCanvas />
      </div>

      {/* MOBILE MENU OFFCANVAS */}
      <div className="offcanvas offcanvas-start canvas-mb" id="mobileMenu">
        <button
          className="icon-close icon-close-popup"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        />
        <div className="mb-canvas-content">
          <div className="mb-body">
            <div className="mb-content-top">
              <ul className="nav-ul-mb" id="wrapper-menu-navigation">
                <li className="nav-mb-item">
                  <Link to="/" className="mb-menu-link text-decoration-none">
                    Home
                  </Link>
                </li>

                <li className="nav-mb-item">
                  <Link to="/shop" className="mb-menu-link text-decoration-none">
                    Shop
                  </Link>
                </li>

                {/* Categories — static item, andar main categories + sub categories */}
                <li className="nav-mb-item">
                  <div
                    className="mb-menu-link nav-item-dropdown d-flex justify-content-between"
                    onClick={() => setMobileCatOpen((v) => !v)}
                    style={{ cursor: "pointer" }}
                  >
                    Categories
                    <span>{mobileCatOpen ? "-" : "+"}</span>
                  </div>

                  {mobileCatOpen && (
                    <ul className="list-unstyled ps-3 mb-0">
                      {categories.map((cat) => (
                        <li key={cat._id}>
                          <div
                            className="mb-menu-link nav-item-dropdown d-flex justify-content-between"
                            onClick={() => handleToggle(cat._id)}
                            style={{ cursor: "pointer" }}
                          >
                            {cat.name}
                            <span>{openId === cat._id ? "-" : "+"}</span>
                          </div>

                          {openId === cat._id && (
                            <div className="dropdown-menu show border-0 ps-2 bg-transparent position-static">
                              <Link
                                to={`/categories/${cat.slugPath}`}
                                className="dropdown-item fw-semibold"
                              >
                                All {cat.name}
                              </Link>
                              {cat.children?.map((sub) => (
                                <Link
                                  key={sub._id}
                                  to={`/categories/${sub.slugPath}`}
                                  className="dropdown-item"
                                >
                                  {sub.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                <li className="nav-mb-item">
                  <Link to="/about-us" className="mb-menu-link text-decoration-none">
                    About
                  </Link>
                </li>

                <li className="nav-mb-item">
                  <Link
                    to="/contact-us"
                    className="mb-menu-link"
                    style={{ textDecoration: "none" }}
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div className="mb-other-content">
              <div className="group-icon">
                <Link
                  to={isLoggedIn ? "/users/dashboard" : "/users/login"}
                  className="shop-btn"
                >
                  <User size={16} className="me-2" /> {isLoggedIn ? "Dashboard" : "Login"}
                </Link>
              </div>

              <div className="mb-contact mb-0 border-bottom">
                <p>
                  Address:
                  <span className="ms-1">
                    {[
                      config?.address?.addressLine,
                      config?.address?.district,
                      config?.address?.state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                    {config?.address?.pincode ? ` - ${config.address.pincode}` : ""}
                  </span>
                </p>
              </div>

              <ul className="pt-3">
                <li>
                  Email:
                  <b className="fw-medium">
                    <a
                      className="text-decoration-none text-secondary"
                      href={`mailto:${config.email}`}
                    >
                      {config.email}
                    </a>
                  </b>
                </li>
                <li>
                  Phone:
                  <b className="fw-medium">
                    <a
                      className="text-decoration-none text-secondary"
                      href={`tel:${config.phone}`}
                    >
                      +91-{config.phone}
                    </a>
                  </b>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}